// y02 Yubiley 50 — milestone-birthday variant.
// A 50th-birthday guest page. Restrained, dignified — half a century, not
// a kid party. Champagne paper, deep wine accent, Bodoni Moda. The hero is
// dominated by the numeral "50"; below it sits the honoree's name + date.
//
// Unique sections beyond the standard set:
//   • "5 ON'YILLIK" — six decade plates, one per ten-year span, each
//     showing year, age and a one-line snapshot.
//   • "Tabriklar" — three quotes from family / friends.

import { useEffect, useRef, useState } from "react";
import type { VariantProps } from "./types";
import { numToWord } from "@/lib/numwords";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";

const DECADES: Array<{ year: string; age: string; line: string }> = [
  { year: "1976", age: "1 yosh", line: "Toshkentda dunyoga keldi — bahor oqshomi." },
  { year: "1986", age: "10 yosh", line: "Maktab. Birinchi kitoblar, birinchi do'stlar." },
  { year: "1996", age: "20 yosh", line: "Universitet. Hayot rejasi tug'ildi." },
  { year: "2006", age: "30 yosh", line: "Oila. Ikki o'g'il dunyoga keldi." },
  { year: "2016", age: "40 yosh", line: "Mehnat va sabr. Ko'p narsani qurdik birga." },
  { year: "2026", age: "50 yosh", line: "Bugun — sevganlar bilan bayram." },
];

const TRIBUTES: Array<{ from: string; relation: string; text: string }> = [
  {
    from: "Munisa opa",
    relation: "rafiqasi",
    text:
      "Yarim asr birga — har kun yangi g'alaba, har oqshom yangi minnatdorchilik. Sen bilan keyingi 50 yil ham qisqa tuyuladi.",
  },
  {
    from: "Jasur",
    relation: "katta o'g'li",
    text: "Otam — bizning eng katta misolimiz. Sabr, mehnat, mehribonlik darslari.",
  },
  {
    from: "Olim aka",
    relation: "akasi",
    text:
      "Bolalikdan birga edik — endi ikkalamiz ham oq sochli bo'ldik, lekin yuragimiz hali yosh.",
  },
];

export function MilestoneBirthdayVariant({ t, isDark, ctx }: VariantProps) {
  const { play } = useSound();
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(false);

  // Derive milestone age from t.names ("Bekzod aka · 50 yosh"). The big hero
  // numeral is split into two glyphs; we split ageNumber into two chars so a
  // 2-digit age (1–99) keeps the same staggered reveal animation. Defaults
  // back to 50 — the design's flagship milestone — when no number present.
  const ageMatch = t.names.match(/(\d{1,3})\s*(yosh|yil)?\s*$/i);
  const ageNumber = ageMatch ? ageMatch[1] : "50";
  const ageDigits = ageNumber.length === 1 ? ["0", ageNumber] : ageNumber.split("").slice(-2);
  const cleanName = t.names.replace(/\s*[·•]?\s*\d+\s*(yosh|yil)?\s*$/i, "").trim() || t.names;

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setRevealed(true);
      return;
    }
    const id = window.setTimeout(() => {
      setRevealed(true);
      play("chime");
    }, 200);
    return () => window.clearTimeout(id);
  }, [play]);

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .v-y02-hero { padding: 64px 18px 48px !important; min-height: auto !important; }
          .v-y02-hero h1 { font-size: clamp(28px, 8vw, 40px) !important; }
          .v-y02-hero-50 { font-size: clamp(120px, 32vw, 200px) !important; }
          .v-y02-decades { grid-template-columns: repeat(2, 1fr) !important; gap: 14px !important; }
          .v-y02-tributes { grid-template-columns: 1fr !important; gap: 18px !important; }
          .v-y02-section { padding: 64px 18px !important; }
        }
        .v-y02-hero button:focus-visible,
        .v-y02-section button:focus-visible,
        .v-y02-section a:focus-visible {
          outline: 2px solid ${t.accent};
          outline-offset: 2px;
        }
      `}</style>
      {/* HERO — massive 50 with names beneath */}
      <section
        className="hero-fill v-y02-hero"
        ref={heroRef}
        style={{
          minHeight: "100vh",
          padding: "120px 32px 80px",
          background: t.bg,
          color: t.ink,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at 50% 60%, ${t.accent}10, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 720 }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 32,
            }}
          >
            {`${numToWord(parseInt(ageNumber, 10), "en").toUpperCase()} YEARS · ${ageNumber} YOSH`}
          </div>
          <div
            aria-label="50"
            className="v-y02-hero-50"
            style={{
              fontFamily: t.font,
              fontWeight: 400,
              fontSize: "clamp(160px, 36vw, 360px)",
              lineHeight: 0.86,
              color: t.ink,
              letterSpacing: "-0.04em",
              fontFeatureSettings: '"lnum" 1, "tnum" 1',
              display: "flex",
              gap: "0.04em",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                opacity: revealed ? 1 : 0,
                transform: revealed ? "none" : "translateY(0.18em)",
                transition: "opacity 700ms ease 0ms, transform 700ms cubic-bezier(.2,.8,.2,1) 0ms",
              }}
            >
              {ageDigits[0]}
            </span>
            <span
              style={{
                opacity: revealed ? 1 : 0,
                transform: revealed ? "none" : "translateY(0.18em)",
                transition: "opacity 700ms ease 220ms, transform 700ms cubic-bezier(.2,.8,.2,1) 220ms",
              }}
            >
              {ageDigits[1]}
            </span>
          </div>
          <h1
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(28px, 5vw, 44px)",
              fontWeight: 400,
              marginTop: 24,
            }}
          >
            {cleanName}
          </h1>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.32em",
              marginTop: 16,
              color: t.ink,
              opacity: 0.85,
            }}
          >
            {t.date.replace(/\./g, " · ")} · {t.venue.toUpperCase()}
          </div>
        </div>
      </section>

      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      <StorySection
        t={t}
        eyebrow="Hayot yo'li"
        title="Yarim asr — minnatdorchilik bilan"
        body={`${cleanName} ${ageNumber} yoshga to'ldi. Mehnat, mehr-oqibat, sabr — bularni biz ota-onamizdan o'rgandik. Bu kechqurun — uning hayotidagi ${ageNumber} ta bahorga, ${ageNumber} ta yozga, ${ageNumber} ta kuzga va ${ageNumber} ta qishga ehtirom uchun. Sevgan insonlari bilan birga uni nishonlashga taklif qilamiz.`}
      />

      {/* UNIQUE: 5 decades */}
      <section
        className="v-y02-section"
        style={{
          padding: "100px 24px",
          borderTop: `1px solid ${t.ink}22`,
          background: t.bg,
          color: t.ink,
        }}
      >
        <div style={{ maxWidth: 920, margin: "0 auto", textAlign: "center" }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 20,
            }}
          >
            5 ON&apos;YILLIK
          </div>
          <h2
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(28px, 5vw, 44px)",
              fontWeight: 400,
              marginBottom: 56,
            }}
          >
            Har bir o&apos;n yil — bir hikoya
          </h2>
          <div
            className="v-y02-decades"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 20,
            }}
          >
            {DECADES.map((d) => (
              <div
                key={d.year}
                style={{
                  padding: "24px 18px",
                  border: `1px solid ${t.accent}55`,
                  background: t.bg,
                  textAlign: "left",
                  position: "relative",
                  boxShadow: `4px 5px 0 -1px ${t.accent}1f`,
                }}
              >
                <div
                  style={{
                    fontFamily: t.font,
                    fontWeight: 400,
                    fontSize: 32,
                    color: t.accent,
                    lineHeight: 1,
                    fontFeatureSettings: '"lnum" 1, "tnum" 1',
                  }}
                >
                  {d.year}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.32em",
                    color: t.ink,
                    opacity: 0.7,
                    marginTop: 6,
                  }}
                >
                  {d.age.toUpperCase()}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-display)",
                    fontStyle: "italic",
                    fontSize: 15,
                    lineHeight: 1.5,
                    marginTop: 10,
                  }}
                >
                  {d.line}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GallerySection t={t} />

      {/* UNIQUE: Tabriklar */}
      <section
        className="v-y02-section"
        style={{
          padding: "100px 24px",
          borderTop: `1px solid ${t.ink}22`,
          background: `${t.accent}10`,
          color: t.ink,
        }}
      >
        <div style={{ maxWidth: 920, margin: "0 auto", textAlign: "center" }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 20,
            }}
          >
            TABRIKLAR
          </div>
          <h2
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(28px, 5vw, 44px)",
              fontWeight: 400,
              marginBottom: 48,
            }}
          >
            Sevganlardan iliq so&apos;zlar
          </h2>
          <div
            className="v-y02-tributes"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 24,
            }}
          >
            {TRIBUTES.map((q) => (
              <figure
                key={q.from}
                style={{
                  margin: 0,
                  padding: "28px 22px",
                  background: t.bg,
                  border: `1px solid ${t.ink}22`,
                  textAlign: "left",
                }}
              >
                <blockquote
                  style={{
                    fontFamily: t.font,
                    fontStyle: "italic",
                    fontSize: 17,
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  &ldquo;{q.text}&rdquo;
                </blockquote>
                <figcaption
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.28em",
                    color: t.accent,
                    marginTop: 18,
                  }}
                >
                  {q.from.toUpperCase()} · {q.relation}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <ScheduleSection
        t={t}
        items={[
          ["18:00", "Mehmonlar yig'ilishi"],
          ["18:30", "Tabriklar boshlanishi"],
          ["19:30", "Maxsus dasturxon"],
          ["20:30", `${cleanName}ning hayot hikoyasi`],
          ["21:30", "Tort va shu'la"],
          ["22:00", "Raqs va musiqa"],
          ["23:30", "Yakuniy so'z"],
        ]}
      />

      <MapSection t={t} isDark={isDark} addressLine={t.venue} />

      <RsvpSection
        t={t}
        isDark={isDark}
        {...ctx.rsvp}
        label={`${ageNumber} yillikni birga nishonlaymiz`}
      />

      <ShareSection t={t} />

      <GuestFooter t={t} />
    </>
  );
}
